import { useEffect, useMemo, useState } from 'react';
import {
  FiMapPin,
  FiPlus,
  FiCreditCard,
  FiTag,
  FiX,
  FiTruck,
  FiTrash2,
} from 'react-icons/fi';
import AddAddressModal from '../checkout/AddAddressModal';
import { Api } from '../../services/api';
import {
  clearCart,
  getCart,
  subscribeToCart,
  updateItemQuantity,
  removeItemFromCart,
} from '../../services/cart';
import { useLanguage } from '../../context/LanguageContext';

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const normalizeAddressList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.addresses)) return payload.addresses;
  return [];
};

const getStoredCustomerId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const meta = JSON.parse(window.localStorage.getItem('myUser'));
    return meta?.customer_id || null;
  } catch {
    return null;
  }
};

const persistPendingCartItems = (list) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    'pendingCartItems',
    JSON.stringify(Array.isArray(list) ? list : [])
  );
};

const getInitialCartItems = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem('pendingCartItems');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed;
      }
    }
  } catch {
    // ignore parse errors and fallback to cart storage
  }
  const cart = getCart();
  const normalized = Array.isArray(cart?.items) ? cart.items : [];
  persistPendingCartItems(normalized);
  return normalized;
};

const getLineQuantity = (item) => Math.max(1, Number(item.quantity || 1));
const getLinePrice = (item) => Number(item.priceUnit || item.price || 0);
const getLineShipping = (item) => Number(item.shipping || 0);

const paymentMethods = [
  {
    id: 'cod',
    labelKey: 'checkout.payment_cod',
    disabled: false,
  },
];

const interpretSuccessValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['success', 'ok', 'true', '200'].includes(normalized)) return true;
    if (['failed', 'fail', 'error', 'false', '400', '500'].includes(normalized))
      return false;
  }
  if (typeof value === 'number') {
    if (value >= 200 && value < 300) return true;
    if (value >= 400) return false;
  }
  return null;
};

const CheckoutPage = () => {
  const [items, setItems] = useState(getInitialCartItems);
  const [customerId, setCustomerId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [addressError, setAddressError] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState('idle');
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [placeOrderError, setPlaceOrderError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState('cod');
  const [initialized, setInitialized] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    setCustomerId(getStoredCustomerId());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const unsubscribe = subscribeToCart((nextCart) => {
      const list = Array.isArray(nextCart?.items) ? nextCart.items : [];
      setItems(list);
      persistPendingCartItems(list);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!customerId) {
      setLoadingAddresses(false);
      setAddressError(t('checkout.address_error_missing_customer'));
      return;
    }

    let cancelled = false;
    const loadAddresses = async () => {
      setLoadingAddresses(true);
      setAddressError(null);
      try {
        const response = await Api.fetchCustomerAddresses(customerId);
        if (cancelled) return;
        const list = normalizeAddressList(response).map((entry) => ({
          ...entry,
          id: entry.addressId || entry.id || entry._id,
        }));
        setAddresses(list);
        if (list.length && !selectedAddressId) {
          const defaultId = list[0]?.id;
          if (defaultId != null) {
            setSelectedAddressId(String(defaultId));
          }
        }
      } catch (error) {
        if (!cancelled) {
          setAddressError(
            error?.message || t('checkout.address_error_generic')
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingAddresses(false);
        }
      }
    };

    loadAddresses();

    return () => {
      cancelled = true;
    };
  }, [customerId, refreshKey, selectedAddressId, t]);

  const selectedAddress = useMemo(
    () =>
      addresses.find(
        (entry) => String(entry.id) === String(selectedAddressId || '')
      ) || null,
    [addresses, selectedAddressId]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + getLinePrice(item) * getLineQuantity(item),
        0
      ),
    [items]
  );

  const shipping = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + getLineShipping(item) * getLineQuantity(item),
        0
      ),
    [items]
  );

  const promoDiscount = useMemo(() => {
    if (!appliedPromo) return 0;
    const amount = Number(appliedPromo.amount || 0);
    return appliedPromo.cashback ? amount : (subtotal * amount) / 100;
  }, [appliedPromo, subtotal]);

  const total = useMemo(() => {
    const raw = subtotal + shipping - promoDiscount;
    return raw < 0 ? 0 : raw;
  }, [subtotal, shipping, promoDiscount]);

  const addressSummary = selectedAddress
    ? (() => {
      const parts = [
        selectedAddress.street ||
        selectedAddress.location_description ||
        selectedAddress.locationDescription ||
        '',
        selectedAddress.building,
        selectedAddress.apartment,
        selectedAddress.city,
      ].filter(Boolean);
      return parts.length ? parts.join(', ') : t('checkout.address_no_details');
    })()
    : t('checkout.address_add_hint');

  const disablePlaceOrder =
    placingOrder || !items.length || !selectedAddress || !customerId;

  const handleRefreshAddresses = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleAddressCreated = (createdAddress) => {
    const id =
      createdAddress?.addressId || createdAddress?.id || createdAddress?._id;
    if (id != null) {
      setSelectedAddressId(String(id));
    }
    setAddressModalOpen(false);
    handleRefreshAddresses();
  };

  const handleQtyChange = (item, delta) => {
    const nextQty = Math.max(1, getLineQuantity(item) + delta);
    updateItemQuantity(item.productId, item.variantId, nextQty);
  };

  const handleRemoveItem = (item) => {
    removeItemFromCart(item.productId, item.variantId);
  };

  useEffect(() => {
    if (!initialized) return;
    if (!items.length && !placingOrder) {
      if (typeof window !== 'undefined') {
        window.location.assign('/cart');
      }
    }
  }, [items, initialized, placingOrder]);

  const applyPromo = async () => {
    if (!promoCode?.trim() || !customerId) return;
    setPromoStatus('checking');
    setPromoError('');
    try {
      const response = await Api.verifyPromoCode({
        code: promoCode.trim(),
        customerId,
      });
      const payload =
        response?.data?.data || response?.data || response || {};
      const valid =
        payload?.valid ??
        payload?.status ??
        (payload?.amount != null || payload?.promo_code_id != null);
      if (!valid) {
        throw new Error(t('checkout.promo_invalid'));
      }
      setAppliedPromo({
        id: payload.promo_code_id || payload.id,
        code: payload.code || promoCode.trim(),
        amount: Number(payload.amount || payload.discount || 0),
        cashback: Boolean(payload.cashback),
      });
      setPromoStatus('valid');
    } catch (error) {
      setPromoStatus('invalid');
      setAppliedPromo(null);
      setPromoError(
        error?.message || t('checkout.promo_error')
      );
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoStatus('idle');
    setPromoError('');
    setPromoCode('');
  };

  const handlePlaceOrder = async () => {
    if (disablePlaceOrder) return;
    if (selectedPayment !== 'cod') {
      setPlaceOrderError(
        t('checkout.place_order_error_online')
      );
      return;
    }
    setPlaceOrderError(null);
    setPlacingOrder(true);

    try {
      const payload = {
        customer: { customerId },
        address:
          selectedAddress?.addressId || selectedAddress?.id
            ? {
              addressId: selectedAddress.addressId || selectedAddress.id,
            }
            : null,
        promoCode: appliedPromo?.id
          ? { promo_code_id: appliedPromo.id }
          : null,
        deliveryCharge: Number(shipping || 0),
        paymentMethod: 'cash',
        notes: notes || null,
        carts: items.map((item) => ({
          product: { productId: item.productId },
          variant: item.variantId ? { variantId: item.variantId } : null,
          quantity: getLineQuantity(item),
          notes: item.notes || '',
          shipping: getLineShipping(item),
        })),
        items: [],
        pickup: false,
      };

      const response = await Api.createOrder(payload);
      const statusHints = [
        interpretSuccessValue(response?.status),
        interpretSuccessValue(response?.success),
        interpretSuccessValue(response?.data?.status),
        interpretSuccessValue(response?.data?.success),
        interpretSuccessValue(response?.statusCode),
        interpretSuccessValue(response?.data?.statusCode),
      ];

      const success =
        statusHints.find((value) => value !== null) ??
        Boolean(response && !response.error);

      if (!success) {
        const message =
          response?.message ||
          response?.data?.message ||
          t('checkout.place_order_error_generic');
        throw new Error(message);
      }

    
      if (typeof window !== 'undefined') {
       
        window.sessionStorage.removeItem('pendingCartItems');
       
      }
      if (typeof window !== 'undefined') {
        window.location.assign('/order-success');

      }
      
  
    } catch (error) {
      setPlaceOrderError(
        error?.message || t('checkout.place_order_error_generic')
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  const shippingLabel =
    shipping > 0 ? formatCurrency(shipping) : t('checkout.free_shipping');

  const renderAddressCard = (address, index) => {
    const id = address.id;
    const isSelected = String(selectedAddressId) === String(id);
    const title =
      address.label ||
      address.name ||
      address.type ||
      `Address ${index + 1}`;
    const phone =
      address.phoneNumber || address.receiverNumber || address.phone || '';
    return (
      <label
        key={id}
        className={`checkout-modern-address-option ${isSelected ? 'is-selected' : ''
          }`}
      >
        <input
          type="radio"
          name="checkout-address"
          value={id}
          checked={isSelected}
          onChange={() => setSelectedAddressId(String(id))}
        />
        <span className="checkout-modern-address-option__radio" />
        <div className="checkout-modern-address-option__body">
          <div className="checkout-modern-address-option__top">
            <span>{title?.toUpperCase()}</span>
            <FiMapPin />
          </div>
         
          {phone && (
            <p className="checkout-modern-address-option__meta">{phone}</p>
          )}
          <p className="checkout-modern-address-option__meta">
            {[
              address.street,
              address.building,
              address.apartment,
              address.city,
              address.country,
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
      </label>
    );
  };

  return (
    <div className="checkout-modern">
      <header className="checkout-modern__header">
        <div>
          <h2>{t('checkout.title')}</h2>
        </div>
      </header>

      <div className="checkout-modern__grid">
        <div className="checkout-modern__main">
          <section className="checkout-modern-card">
            <div className="checkout-modern-card__title">
              <h3>{t('checkout.shipping_address')}</h3>
            </div>
            {loadingAddresses ? (
              <p className="checkout-hint">{t('checkout.address_loading')}</p>
            ) : addressError ? (
              <p className="checkout-error">{addressError}</p>
            ) : addresses.length ? (
              <div className="checkout-modern-addresses">
                {addresses.map((address, index) =>
                  renderAddressCard(address, index)
                )}
              </div>
            ) : (
              <p className="checkout-hint">
                {t('checkout.address_add_hint')}
              </p>
            )}
            <button
              type="button"
              onClick={() => setAddressModalOpen(true)}
              className="checkout-modern-card__link"
            >
              {t('checkout.add_address')}
            </button>
          </section>

          <section className="checkout-modern-card">
            <div className="checkout-modern-card__title">
              <h3>{t('checkout.payment_method')}</h3>
              
            </div>
            <div className="checkout-modern-payment-list">
              {paymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`checkout-modern-payment-option ${selectedPayment === method.id ? 'is-selected' : ''
                    } ${method.disabled ? 'is-disabled' : ''}`}
                  onClick={() => {
                    if (!method.disabled) {
                      setSelectedPayment(method.id);
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="checkout-payment"
                    checked={selectedPayment === method.id}
                    onChange={() => setSelectedPayment(method.id)}
                    disabled={method.disabled}
                  />
                  <span className="checkout-modern-payment-option__radio" />
                  <div>
                    <p>{method.labelKey ? t(method.labelKey) : method.label}</p>
                  </div>
                  {method.badge && (
                    <span className="checkout-modern-payment-option__badge">
                      {method.badge}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </section>

          <section className="checkout-modern-card">
            <div className="checkout-modern-card__title">
              <h3>{t('checkout.delivery_instructions')}</h3>
            </div>
            <textarea
              rows={4}
              placeholder={t('checkout.delivery_placeholder')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </section>

          {placeOrderError && (
            <p className="checkout-error checkout-error--inline">
              {placeOrderError}
            </p>
          )}
          <button
            type="button"
            className="button checkout-modern__cta"
            disabled={disablePlaceOrder}
            onClick={handlePlaceOrder}
          >
            {placingOrder ? t('checkout.placing_order') : t('checkout.place_order')}
          </button>
        </div>

        <aside className="checkout-modern__summary">
          <section className="checkout-modern-summary">
            <h3>{t('checkout.order_summary')}</h3>
            <div className="checkout-modern-summary__items">
              {items.map((item) => {
                const key =
                  item.key ||
                  `${item.productId || ''}:${item.variantId || 'default'}`;
                const qty = getLineQuantity(item);
                const image = item.image;
                return (
                  <article className="checkout-modern-line" key={key}>
                    <div className="checkout-modern-line__media">
                      {image ? (
                        <img src={image} alt={item.name} />
                      ) : (
                        <span className="checkout-modern-line__placeholder">
                          {t('checkout.no_image')}
                        </span>
                      )}
                    </div>
                    <div className="checkout-modern-line__body">
                      <p className="checkout-modern-line__name">
                        {item.name || t('checkout.item_fallback')}
                      </p>
                      {item.variantLabel && (
                        <span className="checkout-modern-line__meta">
                          {item.variantLabel}
                        </span>
                      )}
                      <div className="checkout-modern-line__controls">
                        <div className="quantity-stepper">
                          <button
                            type="button"
                            onClick={() => handleQtyChange(item, -1)}
                            aria-label={t('checkout.aria_decrease')}
                          >
                            -
                          </button>
                          <span>{qty}</span>
                          <button
                            type="button"
                            onClick={() => handleQtyChange(item, 1)}
                            aria-label={t('checkout.aria_increase')}
                          >
                            +
                          </button>
                        </div>

                      </div>
                    </div>
                    <div className="checkout-modern-line__price">
                      {formatCurrency(getLinePrice(item) * qty)}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="checkout-modern-promo">
              <div className="checkout-modern-promo__field">
                <FiTag />
                <input
                  type="text"
                  placeholder={t('checkout.promo_placeholder')}
                  value={promoCode}
                  onChange={(event) => {
                    setPromoCode(event.target.value.toUpperCase());
                    setPromoStatus('idle');
                    setPromoError('');
                  }}
                />
                <button
                  type="button"
                  onClick={applyPromo}
                  disabled={promoStatus === 'checking' || !promoCode.trim()}
                >
                  {promoStatus === 'checking'
                    ? t('checkout.promo_checking')
                    : t('checkout.promo_apply')}
                </button>
              </div>
              {promoError && (
                <p className="checkout-error">{promoError}</p>
              )}
              {appliedPromo && (
                <div className="checkout-modern-promo-badge">
                  <span>
                    {appliedPromo.code}{' '}
                    <strong>
                      {appliedPromo.cashback
                        ? `- ${formatCurrency(appliedPromo.amount)}`
                        : `- ${appliedPromo.amount}%`}
                    </strong>
                  </span>
                  <button
                    type="button"
                    onClick={removePromo}
                    aria-label={t('checkout.promo_remove')}
                  >
                    <FiX />
                  </button>
                </div>
              )}
            </div>

            <div className="checkout-modern-summary__totals">
              <div>
                <span>{t('checkout.summary_subtotal')}</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              <hr className="order-summary-divider" />
              <div>
                <span>{t('checkout.summary_shipping')}</span>
                <strong>{shippingLabel}</strong>
              </div>
              <hr className="order-summary-divider" />
              {appliedPromo && (

                <div>
                  <span>{t('checkout.summary_promo')}</span>
                  <strong>-{formatCurrency(promoDiscount)}</strong>



                </div>

              )}

              {appliedPromo &&  <hr className="order-summary-divider" />}
            </div>
            <div className="checkout-modern-summary__total">
              <span>{t('checkout.summary_total')}</span>
              <strong>{formatCurrency(total)}</strong>
            </div>


          </section>
        </aside>
      </div>

      <AddAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        customerId={customerId}
        onCreated={handleAddressCreated}
      />
    </div>
  );
};

export default CheckoutPage;
